package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.SharedPageResponse
import com.advaita.cloud.drive.ui.api.ApiException
import com.advaita.cloud.drive.ui.api.CreateTarget
import com.advaita.cloud.drive.ui.api.ShareApiException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay

/**
 * ADV-1529: where an extract goes, as the extract dialog returns it. [beside] is true for the
 * archive's own folder (the request then names no destination), and [target] is that folder.
 * Else [target] is the folder the person picked.
 */
data class ExtractChoice(val target: CreateTarget, val beside: Boolean)

/**
 * ADV-1529: a place the extract dialog can stand.
 *
 * - [Own]: the caller's own folder at [Own.path] (the My Drive tab).
 * - [SharedRoots]: the shared folders the caller may upload into (the Shared with me tab's top).
 * - [Shared]: a shared folder, [Shared.trail] from its shared root down.
 */
sealed interface ExtractPickerAt {
    data class Own(val path: String) : ExtractPickerAt
    data object SharedRoots : ExtractPickerAt
    data class Shared(val trail: List<FileDto>) : ExtractPickerAt
}

/** The place as an extract destination, or null for the shared list, which is no folder. */
fun ExtractPickerAt.target(): CreateTarget? = when (this) {
    is ExtractPickerAt.Own -> CreateTarget.MyDrive(path)
    is ExtractPickerAt.Shared -> trail.last().let { CreateTarget.SharedFolder(it.id, it.displayName) }
    ExtractPickerAt.SharedRoots -> null
}

/** [folder], a child row of the place, as the next place. */
internal fun ExtractPickerAt.into(folder: FileDto): ExtractPickerAt = when (this) {
    is ExtractPickerAt.Own -> ExtractPickerAt.Own(folder.logicalPath)
    is ExtractPickerAt.Shared -> ExtractPickerAt.Shared(trail + folder)
    ExtractPickerAt.SharedRoots -> ExtractPickerAt.Shared(listOf(folder))
}

/** One level up, or null at the top of a tab. */
internal fun ExtractPickerAt.up(): ExtractPickerAt? = when (this) {
    is ExtractPickerAt.Own ->
        if (path == "/") null else ExtractPickerAt.Own(path.trimEnd('/').substringBeforeLast('/').ifEmpty { "/" })
    is ExtractPickerAt.Shared -> if (trail.size == 1) ExtractPickerAt.SharedRoots else ExtractPickerAt.Shared(trail.dropLast(1))
    ExtractPickerAt.SharedRoots -> null
}

/** True on the Shared with me tab. */
internal val ExtractPickerAt.onSharedTab: Boolean get() = this !is ExtractPickerAt.Own

/** The place's own name: the folder's, or the tab's at its top. */
internal fun ExtractPickerAt.label(): String = when (this) {
    is ExtractPickerAt.Own -> path.trimEnd('/').substringAfterLast('/').ifEmpty { MY_DRIVE }
    ExtractPickerAt.SharedRoots -> SHARED_WITH_ME
    is ExtractPickerAt.Shared -> trail.last().displayName
}

/** The place's whole path, for the destination field: "My Drive / Runs / raw". */
internal fun ExtractPickerAt.fullLabel(): String = when (this) {
    is ExtractPickerAt.Own -> (listOf(MY_DRIVE) + path.split('/').filter { it.isNotEmpty() }).joinToString(" / ")
    ExtractPickerAt.SharedRoots -> SHARED_WITH_ME
    is ExtractPickerAt.Shared -> (listOf(SHARED_WITH_ME) + trail.map { it.displayName }).joinToString(" / ")
}

/**
 * The shared roots a picker offers: folders whose grant holds `upload`. A file shared on its own
 * is no destination, and a Viewer's folder takes no new rows. When the grants could not be read,
 * [SharedPageResponse.shares] is empty and nothing is offered: the server decides `upload`, and an
 * offer it then refuses helps nobody.
 */
internal fun uploadableSharedFolders(page: SharedPageResponse): List<FileDto> =
    page.items.filter { item ->
        item.kind == FileKind.FOLDER &&
            page.shares[item.id]?.let { g -> "upload" in g.effectiveCapabilities.ifEmpty { g.capabilities } } == true
    }

/**
 * Runs [read]. When [fresh] (a folder the dialog just made in a shared folder), a `404` means
 * "not registered yet", so it waits [pollMs] and reads again, up to [reads] more times, as
 * [SharedWithMeStore.awaitRows] does for the main view's creates. Cancellation always propagates.
 */
internal suspend fun <T> awaitRegistered(
    fresh: Boolean,
    reads: Int = ROW_WAIT_READS,
    pollMs: Long = SHARED_POLL_MS,
    onWaiting: (Boolean) -> Unit = {},
    read: suspend () -> T,
): T {
    var left = if (fresh) reads else 0
    try {
        while (true) {
            try {
                return read()
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                if (left-- <= 0 || !e.isNotFound()) throw e
                onWaiting(true)
                delay(pollMs)
            }
        }
    } finally {
        onWaiting(false)
    }
}

private fun Throwable.isNotFound(): Boolean =
    (this is ApiException && status == 404) || (this is ShareApiException && status == 404)

/** A short name for [target] in the toast: the folder's name, or "My Drive" at the root. */
internal fun destinationName(target: CreateTarget): String = when (target) {
    is CreateTarget.MyDrive -> ExtractPickerAt.Own(target.path).label()
    is CreateTarget.SharedFolder -> target.name
}

internal const val MY_DRIVE = "My Drive"
internal const val SHARED_WITH_ME = "Shared with me"

/**
 * Where the dialog's browser starts: the archive's own folder ([besideAt]) when the server allows
 * it, else the caller's My Drive. Only the first is chosen for the caller ([ExtractToUiState.chosen]).
 */
internal fun extractStart(besideAt: ExtractPickerAt?, besideAllowed: Boolean): ExtractPickerAt =
    besideAt?.takeIf { besideAllowed } ?: ExtractPickerAt.Own("/")

/**
 * The extract dialog's live state. It opens compact: one destination field and a Change button.
 * [browsing] opens the folder browser in place. [allowed] is the server's answer for [at]: null
 * while it is asked, and for the shared list.
 *
 * [chosen]: beside the archive is chosen for the caller when the server allows it. Any other
 * place must be the caller's own pick (Tim, 2026-10-05): with no beside, the dialog opens with
 * no folder chosen, and the place counts only once the caller has opened the browser on it.
 */
class ExtractToUiState(val besideAt: ExtractPickerAt?, val besideAllowed: Boolean) {
    var at by mutableStateOf(extractStart(besideAt, besideAllowed))
    var browsing by mutableStateOf(false)
    val chosen: Boolean get() = browsing || besideAllowed
    var children by mutableStateOf<List<FileDto>>(emptyList())
    var loading by mutableStateOf(false)
    var error by mutableStateOf<String?>(null)
    var allowed by mutableStateOf<Boolean?>(null)

    /** The new-folder name being typed, or null while no new folder is open. */
    var newFolderName by mutableStateOf<String?>(null)
    var creating by mutableStateOf(false)

    /**
     * A folder this dialog just made inside a shared folder, by id. Until the vault registers it,
     * the server answers `404` for it (the share's owner owns it), so its reads wait ([awaitRegistered]).
     */
    var freshSharedFolderId by mutableStateOf<String?>(null)

    /** True while the browser waits for [freshSharedFolderId] to be registered. */
    var registering by mutableStateOf(false)

    /** True when [at] is the folder this dialog just made in a shared folder. */
    val atFreshSharedFolder: Boolean
        get() = (at as? ExtractPickerAt.Shared)?.trail?.last()?.id?.let { it == freshSharedFolderId } == true

    /** True where the answer is already known: the archive's own folder, which the server allowed. */
    val atBeside: Boolean get() = besideAllowed && at == besideAt

    /** The dialog's answer for its main button, or null while nothing usable is chosen. */
    fun choice(): ExtractChoice? =
        at.target()?.takeIf { chosen && allowed == true }?.let { ExtractChoice(it, beside = atBeside) }

    fun go(next: ExtractPickerAt) {
        at = next
        newFolderName = null
        error = null
        registering = false
    }
}
